import React, { useEffect, useState } from 'react';
import { css } from '@emotion/core';
import { useSelector } from 'react-redux';

import { Loader } from '../../shared/loader/loader.component';
import { selectCurrentLocation } from '../../../redux/actions/location';
import { formatDateFromUnixTimestamp } from '../../../helpers/utils';
import { Table } from 'reactstrap';
import { SuccessSVG } from './success-svg';
import { PhoneNumberLookup } from './phone-number-lookup';
import { portingHubStyles } from './porting-hub.styles';
import moment from 'moment';
import { PrimaryButton, SecondaryButton, Modal, Text } from '@weave/design-system';
import { SalesforcePortOrderForm } from './salesforce-port-order-form.component';
import { SearchPortingData } from './search-porting-data.component';

import { PortingData } from '@weave/schema-gen-ts/dist/schemas/phone/porting/porting-data/v1/porting_data_service.pb';
import { PortStatus } from '@weave/schema-gen-ts/dist/shared/porting/v1/enums.pb';
import { Page } from '../../shared';
import { useDebugMode } from '../../debug/use-debug-mode';
import { usePortOrderStatus } from '../hooks/use-port-order-status';
import { useHistory } from 'react-router-dom';
import { ONBOARDING, injectParams } from '../../../constants/paths';
import {
  getPortingStatusColor,
  getPortOrderType,
  getStatusForPorting,
} from '../utils/porting-utils';
import { PortOut } from '../port-out/port-out';
import { PortingQueries } from 'apis/porting/porting.queries';

export const PortingHub = () => {
  const [searchPortResults, setSearchPortResults] = useState<PortingData[]>([]);

  const history = useHistory();

  const {
    portOrderStatus: currentPortOrderStatus,
    isLoading: isPortOrderStatusLoading,
    getPortOrderStatus,
    clearPortOrderStatus,
  } = usePortOrderStatus();

  const { isDebugModeEnabled } = useDebugMode();

  const currentLocation = useSelector(selectCurrentLocation);

  const {
    data: portOrders,
    isLoading: isLoadingPortingData,
    isRefetching: isRefetchingPortingData,
    refetch: refetchPortOrders,
  } = PortingQueries.useGetPortOrderList(
    {
      locationId: currentLocation?.LocationID,
    },
    {
      select: (response) => {
        const portingData = response.portingData ?? [];
        if (isDebugModeEnabled) {
          // Do not filter the data if debug mode is enabled.
          return portingData;
        } else {
          return portingData?.filter(({ portingRequests }) =>
            portingRequests?.every(
              (request) =>
                request.portingStatus !== PortStatus.PORT_STATUS_DRAFT_INCOMPLETE
            )
          );
        }
      },
      staleTime: 0,
      refetchOnMount: true,
    }
  );

  const showReviewButtonStatus = [
    PortStatus.PORT_STATUS_UNSPECIFIED,
    PortStatus.PORT_STATUS_DRAFT,
    PortStatus.PORT_STATUS_VALIDATION_FAILED,
  ];

  const handleCheckStatus = (portOrder: PortingData) => {
    getPortOrderStatus(portOrder.id ?? '');
  };

  const clearPortingStatus = () => {
    clearPortOrderStatus();
  };

  const goToV1 = () => {
    history.push(injectParams(ONBOARDING.porting, { id: currentLocation?.LocationID }));
  };

  const navigateToPortOrder = (portOrder: PortingData) => {
    history.push(
      injectParams(ONBOARDING.portingV2Detail, {
        id: currentLocation?.LocationID,
        portingDataId: portOrder.id,
      })
    );
  };

  const navigateToCreatePortOrder = () => {
    history.push(
      injectParams(ONBOARDING.portingV2CreateOrder, { id: currentLocation?.LocationID })
    );
  };

  useEffect(() => {
    setSearchPortResults(portOrders ?? []);
  }, [portOrders]);

  if (!currentLocation) {
    return null;
  }
  if (isLoadingPortingData || isRefetchingPortingData) {
    return (
      <div className={portingHubStyles.loading}>
        <Loader />
      </div>
    );
  }

  return (
    <Page
      title={`Port Orders`}
      headerActions={
        <div className={portingHubStyles.portContainerButtons}>
          <div className={portingHubStyles.newPortOrder}>
            <PrimaryButton color="blue" onClick={navigateToCreatePortOrder}>
              New Port Order
            </PrimaryButton>
          </div>
          <div className={portingHubStyles.portRefresh}>
            <PrimaryButton color="blue" onClick={() => refetchPortOrders()}>
              Refresh
            </PrimaryButton>
          </div>
          {isDebugModeEnabled && (
            <div className={portingHubStyles.portRefresh}>
              <PrimaryButton color="blue" onClick={goToV1}>
                Go To V1
              </PrimaryButton>
            </div>
          )}
        </div>
      }
      subtitle="View all port orders for this location below"
    >
      <div className={portingHubStyles.portContainer}>
        <SalesforcePortOrderForm getPortingData={refetchPortOrders} />
        <SearchPortingData
          portOrders={portOrders ?? []}
          setSearchPortResult={setSearchPortResults}
        />
      </div>
      <div
        css={css`
          margin-right: 20px;
        `}
      >
        {/*// @ts-ignore */}
        <Table borderless="true" className={portingHubStyles.tableView}>
          <colgroup>
            <col style={{ width: '200px' }} />
            <col style={{ width: '200px' }} />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
          </colgroup>
          <thead>
            <tr className={portingHubStyles.tableHeadRow}>
              <th>Port Order Number</th>
              <th>Port Order Name</th>
              <th>Created Date</th>
              <th>Port Type</th>
              <th>Requested Date</th>
              <th>FOC Date</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>

          <tbody className={portingHubStyles.tableBody}>
            {searchPortResults?.map((port, index) => {
              const portingRequest = port?.portingRequests?.[0];
              if (!portingRequest) {
                return null;
              }

              return (
                <tr key={index}>
                  {/* Port Order Number */}
                  <td>{portingRequest.portOrderNumber}</td>

                  {/* Port Order Name */}
                  <td>{port.sfPortOrderName}</td>

                  {/* Created Date */}
                  <td>
                    {portingRequest.createdAt &&
                      moment(portingRequest.createdAt).utc().format('MM/DD/YY')}
                  </td>

                  {/* Port Type */}
                  <td>{getPortOrderType(port)}</td>

                  {/* Requested Date */}
                  <td>
                    {portingRequest.requestedFirmOrderCommitmentDate
                      ? moment(portingRequest.requestedFirmOrderCommitmentDate)
                          .utc()
                          .format('MM/DD/YY')
                      : ''}
                  </td>

                  {/*FOC Date */}
                  <td>
                    {portingRequest.firmOrderCommitmentDate
                      ? moment(portingRequest.firmOrderCommitmentDate)
                          .utc()
                          .format('MM/DD/YY')
                      : ''}
                  </td>

                  {/* Status */}
                  <td>
                    <div className={portingHubStyles.dotColumn}>
                      <div
                        css={(theme) => css`
                          width: 8px;
                          height: 8px;
                          margin: ${theme.spacing(1)};
                          border: 1px solid ${getPortingStatusColor(portingRequest)};
                          background-color: ${getPortingStatusColor(portingRequest)};
                          border-radius: 50%;
                        `}
                      />
                      <span>{getStatusForPorting(port)}</span>
                    </div>
                  </td>

                  {/* Actions */}
                  <td>
                    <div className={portingHubStyles.tableActions}>
                      {portingRequest.portingStatus &&
                      !showReviewButtonStatus.includes(portingRequest.portingStatus) ? (
                        <>
                          {portingRequest.portingStatus !==
                            PortStatus.PORT_STATUS_CANCELLED && (
                            <div className={portingHubStyles.portContainerButtons}>
                              <div className={portingHubStyles.newPortOrder}>
                                <PrimaryButton
                                  color="blue"
                                  onClick={() => handleCheckStatus(port)}
                                >
                                  Check Status
                                </PrimaryButton>
                              </div>
                            </div>
                          )}
                          <div
                            className={portingHubStyles.portRefresh}
                            css={css`
                              text-align: center;
                              cursor: pointer;
                            `}
                            onClick={() => {
                              navigateToPortOrder(port);
                            }}
                          >
                            View Info
                          </div>
                        </>
                      ) : (
                        <div className={portingHubStyles.portContainerButtons}>
                          <div className={portingHubStyles.newPortOrder}>
                            <SecondaryButton
                              className={portingHubStyles.reviewButton}
                              color="gray"
                              onClick={() => {
                                navigateToPortOrder(port);
                              }}
                            >
                              Review
                            </SecondaryButton>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
      {!searchPortResults.length && (
        <Text
          css={css`
            text-align: center;
          `}
        >
          No Result Found
        </Text>
      )}

      {portOrders?.length === 0 && (
        <div
          css={css`
            text-align: center;
            margin-bottom: 14px;
          `}
        >
          There are no active ports for this location.
        </div>
      )}
      <PhoneNumberLookup />
      <PortOut />
      <Modal
        onClose={clearPortingStatus}
        css={(theme) =>
          css`
            padding: ${theme.spacing(2)};
          `
        }
        show={
          isPortOrderStatusLoading ||
          (!isPortOrderStatusLoading && !!currentPortOrderStatus) ||
          false
        }
      >
        {isPortOrderStatusLoading ? (
          <div>
            <div className={portingHubStyles.loading}>
              <Loader size="large" />
            </div>
            <div className={portingHubStyles.pingingBandwidth}>
              Pinging {currentPortOrderStatus?.provider ?? 'Provider'}...
            </div>
          </div>
        ) : (
          <div>
            <div className={portingHubStyles.loading}>
              <SuccessSVG />
            </div>
            <div className={portingHubStyles.statusComplete}>Status Check Complete!</div>
            {currentPortOrderStatus && (
              <div className={portingHubStyles.progress}>
                {currentPortOrderStatus.progressStatus}
              </div>
            )}

            {currentPortOrderStatus && (
              <div className={portingHubStyles.portStatusStyle}>
                {currentPortOrderStatus.orderHistories &&
                  currentPortOrderStatus.orderHistories.map((orderHistory, index) => {
                    if (
                      index ===
                        (currentPortOrderStatus.orderHistories?.length ?? 0) - 1 ||
                      index === (currentPortOrderStatus.orderHistories?.length ?? 0) - 2
                    ) {
                      return (
                        <div
                          key={index}
                          css={css`
                            margin-bottom: 10px;
                          `}
                        >
                          <span
                            css={css`
                              font-weight: bold;
                            `}
                          >
                            {formatDateFromUnixTimestamp(
                              orderHistory.orderDate ?? '',
                              'MM/dd/yy'
                            )}
                            :
                          </span>{' '}
                          {orderHistory.note}
                        </div>
                      );
                    }
                    return null;
                  })}

                {currentPortOrderStatus.orderNotes &&
                  currentPortOrderStatus.orderNotes.map((orderNote, index) => {
                    if (
                      index === (currentPortOrderStatus.orderNotes?.length ?? 0) - 1 ||
                      index === (currentPortOrderStatus.orderNotes?.length ?? 0) - 2
                    ) {
                      return (
                        <div
                          key={index}
                          css={css`
                            margin-bottom: 10px;
                          `}
                        >
                          <span
                            css={css`
                              font-weight: bold;
                            `}
                          >
                            {formatDateFromUnixTimestamp(
                              orderNote.lastDateModifier ?? '',
                              'MM/dd/yy'
                            )}
                            :
                          </span>{' '}
                          {orderNote.description}
                        </div>
                      );
                    }
                    return null;
                  })}
              </div>
            )}
            <PrimaryButton
              className={portingHubStyles.gotItButton}
              color="blue"
              size="large"
              onClick={() => {
                clearPortingStatus();
              }}
            >
              Got it
            </PrimaryButton>
          </div>
        )}
      </Modal>
    </Page>
  );
};
